/* jshint esversion: 6 */
/* jshint esversion: 8 */
/* jshint ignore:start */
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { showTost } from "../showTost";
import { FaStar, FaBolt, FaTimes, FaPlusCircle, FaClock } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import PackageIcon from "../images/icons/buycoin-package.png";
import { MyContext } from "../Context/MyProvider";

const EngagementModal = ({ isOpen, onClose, baseUrl, initialType = "SUPER_LIKE" }) => {
  const navigate = useNavigate();
  const { isSuperLikeMasterEnabled = true, isBoostMasterEnabled = true } = useContext(MyContext) || {};
  const [selectedType, setSelectedType] = useState(initialType);

  useEffect(() => {
    if (!isSuperLikeMasterEnabled && isBoostMasterEnabled) {
      setSelectedType("BOOST");
    } else if (!isBoostMasterEnabled && isSuperLikeMasterEnabled) {
      setSelectedType("SUPER_LIKE");
    } else {
      setSelectedType(initialType);
    }
  }, [initialType, isSuperLikeMasterEnabled, isBoostMasterEnabled]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userCoins, setUserCoins] = useState(0);
  const [balance, setBalance] = useState({
    super_likes: 0,
    boosts: 0,
    super_boosts: 0,
    is_boost_active: "0",
    boost_remaining_seconds: 0,
  });

  const getUserId = () => {
    try {
      const directId = localStorage.getItem("UserId");
      if (directId) return directId;
      const regUser = localStorage.getItem("Register_User");
      if (regUser) {
        const parsed = JSON.parse(regUser);
        if (parsed && (parsed.id || parsed.uid)) return String(parsed.id || parsed.uid);
      }
      const userLogin = localStorage.getItem("UserLogin");
      if (userLogin) {
        const parsed = JSON.parse(userLogin);
        if (parsed && (parsed.id || parsed.uid)) return String(parsed.id || parsed.uid);
      }
      return null;
    } catch {
      return null;
    }
  };

  const getUserToken = () => {
    try {
      const token = localStorage.getItem("token");
      if (token) return token;
      const regUser = localStorage.getItem("Register_User");
      if (regUser) {
        const parsed = JSON.parse(regUser);
        if (parsed && parsed.token) return parsed.token;
      }
      return "";
    } catch {
      return "";
    }
  };

  const getApiUrl = (endpoint) => {
    let base = (baseUrl || "").trim().replace(/\/+$/, "");
    if (base.toLowerCase().endsWith("/api")) {
      base = base.substring(0, base.length - 4);
    }
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    return base ? `${base}${cleanEndpoint}` : cleanEndpoint;
  };

  const fetchProducts = async () => {
    try {
      const url = getApiUrl("/api/engagement_products");
      const res = await axios.get(url);
      if (res.data && (res.data.Result === "true" || res.data.products)) {
        setProducts(res.data.products || []);
      }
    } catch (err) {
      console.error("Error fetching engagement products:", err);
      try {
        const fallbackRes = await axios.get("/api/engagement_products");
        if (fallbackRes.data && fallbackRes.data.products) {
          setProducts(fallbackRes.data.products);
        }
      } catch (e) {}
    }
  };

  const fetchBalanceAndCoins = async () => {
    const uid = getUserId();
    if (!uid) return;
    const token = getUserToken();
    const headers = { authorization: token ? `Bearer ${token}` : "" };
    try {
      // Fetch engagement balance
      const balanceUrl = getApiUrl("/api/engagement_balance");
      const balanceRes = await axios.post(balanceUrl, { uid }, { headers });
      if (balanceRes.data && balanceRes.data.Result === "true") {
        setBalance({
          super_likes: balanceRes.data.super_likes || 0,
          boosts: balanceRes.data.boosts || 0,
          super_boosts: balanceRes.data.super_boosts || 0,
          is_boost_active: balanceRes.data.is_boost_active || "0",
          boost_remaining_seconds: balanceRes.data.boost_remaining_seconds || 0,
        });
      }

      // Fetch user coins from coin_report
      const coinUrl = getApiUrl("/api/coin_report");
      const coinRes = await axios.post(coinUrl, { uid }, { headers });
      if (coinRes.data && coinRes.data.coin !== undefined) {
        setUserCoins(parseInt(coinRes.data.coin, 10) || 0);
      }
    } catch (err) {
      console.error("Error fetching balance and coins:", err);
    }
  };

  // Live countdown timer for active boost
  useEffect(() => {
    let interval = null;
    if (balance.is_boost_active === "1" && balance.boost_remaining_seconds > 0) {
      interval = setInterval(() => {
        setBalance((prev) => {
          if (prev.boost_remaining_seconds <= 1) {
            return { ...prev, is_boost_active: "0", boost_remaining_seconds: 0 };
          }
          return { ...prev, boost_remaining_seconds: prev.boost_remaining_seconds - 1 };
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [balance.is_boost_active, balance.boost_remaining_seconds]);

  useEffect(() => {
    if (isOpen) {
      setSelectedType(initialType);
      fetchProducts();
      fetchBalanceAndCoins();
    }
  }, [isOpen, initialType]);

  if (!isOpen) return null;

  const handlePurchase = async (p) => {
    const uid = getUserId();
    if (!uid) {
      showTost({ title: "Please log in first!" });
      return;
    }

    if (userCoins < p.coin_price) {
      showTost({ title: `Insufficient coins! Redirecting to Coin Packages...` });
      navigate("/buycoin");
      onClose();
      return;
    }

    setLoading(true);
    const token = getUserToken();
    const headers = { authorization: token ? `Bearer ${token}` : "" };

    try {
      const url = getApiUrl("/api/purchase_engagement_product");
      const res = await axios.post(
        url,
        {
          uid,
          product_id: p.id,
        },
        { headers }
      );
      
      showTost({ title: res.data.ResponseMsg || "Purchase completed! Product added to your inventory." });

      if (res.data.Result === "true") {
        if (res.data.remaining_coins !== undefined) {
          setUserCoins(Number(res.data.remaining_coins));
        }
        await fetchBalanceAndCoins();
      }
    } catch (err) {
      showTost({ title: err.response?.data?.ResponseMsg || "Purchase failed!" });
    } finally {
      setLoading(false);
    }
  };

  const handleActivateBoost = async (productType = "BOOST", durationMinutes = 30) => {
    const uid = getUserId();
    if (!uid) return;
    setLoading(true);
    const token = getUserToken();
    const headers = { authorization: token ? `Bearer ${token}` : "" };

    try {
      const url = getApiUrl("/api/activate_boost");
      const res = await axios.post(
        url,
        {
          uid,
          product_type: productType,
          duration_minutes: durationMinutes,
        },
        { headers }
      );
      showTost({ title: res.data.ResponseMsg || "Boost activated!" });
      if (res.data.Result === "true") {
        await fetchBalanceAndCoins();
      }
    } catch (err) {
      showTost({ title: err.response?.data?.ResponseMsg || "Boost activation failed!" });
    } finally {
      setLoading(false);
    }
  };

  const formatRemainingTime = (totalSeconds) => {
    if (totalSeconds <= 0) return "00m 00s";
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hrs > 0) {
      return `${hrs}h ${mins}m ${secs < 10 ? "0" : ""}${secs}s`;
    }
    return `${mins}m ${secs < 10 ? "0" : ""}${secs}s`;
  };

  const filteredProducts = products.filter((p) => {
    const type = String(p.product_type || "").toUpperCase();
    if (selectedType === "SUPER_LIKE") {
      return type === "SUPER_LIKE";
    }
    return type === "BOOST" || type === "SUPER_BOOST";
  });

  if (!isOpen || (!isSuperLikeMasterEnabled && !isBoostMasterEnabled)) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4 animate-fade-in">
      <div className="bg-white text-gray-900 w-full max-w-md rounded-3xl p-6 relative shadow-2xl border border-purple-100 max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header bar with Title, Coin Balance & Close Button */}
        <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
          <div className="flex items-center gap-2">
            <h3 className="font-extrabold text-lg text-purple-950 flex items-center gap-1.5">
              {selectedType === "SUPER_LIKE" ? "⭐ Super Likes" : "⚡ Profile Boost"}
            </h3>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-amber-50 text-amber-900 border border-amber-200 px-2.5 py-1 rounded-full text-xs font-bold shadow-sm">
              <span className="text-amber-500 font-extrabold">🪙</span>
              <span>{userCoins}</span>
              <button
                onClick={() => {
                  onClose();
                  navigate("/buycoin");
                }}
                className="ml-1 text-amber-600 hover:text-amber-800 transition-colors"
                title="Get Coins"
              >
                <FaPlusCircle className="text-xs" />
              </button>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-900 flex items-center justify-center transition-colors"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>
        </div>

        {/* Tab Switcher */}
        {isSuperLikeMasterEnabled && isBoostMasterEnabled && (
          <div className="flex justify-center gap-2 mb-4 bg-gray-100 p-1.5 rounded-2xl">
            <button
              onClick={() => setSelectedType("SUPER_LIKE")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl font-bold text-sm transition-all ${
                selectedType === "SUPER_LIKE"
                  ? "bg-amber-500 text-white shadow-md"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <FaStar className={selectedType === "SUPER_LIKE" ? "text-white" : "text-amber-500"} />
              <span>Super Likes ({balance.super_likes})</span>
            </button>
            <button
              onClick={() => setSelectedType("BOOST")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl font-bold text-sm transition-all ${
                selectedType === "BOOST"
                  ? "bg-purple-600 text-white shadow-md"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <FaBolt className={selectedType === "BOOST" ? "text-white" : "text-purple-600"} />
              <span>Boosts ({balance.boosts + balance.super_boosts})</span>
            </button>
          </div>
        )}

        {/* Header Banner Text */}
        <div className="text-center mb-4 bg-purple-50/60 p-3 rounded-2xl border border-purple-100">
          <h4 className="font-bold text-gray-900 text-base">
            {selectedType === "SUPER_LIKE" ? "Stand Out & Get Matched!" : "Get Up To 10x More Profile Views!"}
          </h4>
          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
            {selectedType === "SUPER_LIKE"
              ? "Send a Super Like to get 3x higher match rate and grab instant attention!"
              : "Boost your profile to stay at the top of nearby candidate feeds!"}
          </p>
        </div>

        {/* Active Boost Banner & Live Timer */}
        {selectedType === "BOOST" && balance.is_boost_active === "1" && (
          <div className="bg-purple-100 border border-purple-300 rounded-2xl p-3.5 text-center mb-4 shadow-sm">
            <div className="font-extrabold text-purple-900 text-sm flex items-center justify-center gap-2 mb-1">
              <FaBolt className="text-amber-500 animate-bounce" />
              <span>⚡ Boost is Currently Active!</span>
            </div>
            <div className="inline-flex items-center gap-1.5 bg-purple-600 text-white font-extrabold text-xs px-3 py-1 rounded-full shadow-sm my-1">
              <FaClock className="text-amber-300 animate-spin-slow" />
              <span>{formatRemainingTime(balance.boost_remaining_seconds)} remaining</span>
            </div>
            <p className="text-[11px] text-purple-700 font-medium mt-1">
              Activating an additional boost will <strong className="text-purple-950">ADD +30 minutes</strong> directly to your current remaining time!
            </p>
          </div>
        )}

        {/* Activate Boost Button (Active or Inactive) */}
        {selectedType === "BOOST" && (balance.boosts > 0 || balance.super_boosts > 0) && (
          <button
            onClick={() => handleActivateBoost("BOOST")}
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold py-3 px-4 rounded-2xl shadow-lg mb-4 flex items-center justify-center gap-2 transition-all text-sm active:scale-98"
          >
            <FaBolt className="text-amber-300" />
            {balance.is_boost_active === "1"
              ? `⚡ Add +30 Mins Boost Now (${balance.boosts} Available)`
              : `⚡ Activate 1 Boost Now (${balance.boosts} Available)`}
          </button>
        )}

        {/* Products List */}
        <div className="overflow-y-auto flex-1 pr-1 space-y-3 min-h-[160px]">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 font-semibold text-sm">No packages available right now.</p>
            </div>
          ) : (
            filteredProducts.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between bg-white p-3.5 rounded-2xl border border-gray-200 shadow-sm hover:border-purple-300 transition-all gap-2"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0 ${
                      selectedType === "SUPER_LIKE"
                        ? "bg-amber-100 text-amber-600"
                        : "bg-purple-100 text-purple-600"
                    }`}
                  >
                    {selectedType === "SUPER_LIKE" ? <FaStar /> : <FaBolt />}
                  </div>
                  <div className="min-w-0">
                    <h5 className="font-bold text-gray-900 text-sm mb-0.5 truncate">{p.title}</h5>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-purple-900 flex items-center gap-1 bg-[#f4e7ff] px-2 py-0.5 rounded-md border border-purple-200">
                        {p.coin_price} <img src={PackageIcon} className="w-[14px] h-[14px] object-contain" alt="coin" />
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handlePurchase(p)}
                  disabled={loading}
                  className={`w-[130px] min-w-[130px] h-[38px] flex items-center justify-center gap-1.5 font-bold rounded-xl text-xs transition-all shadow-md shrink-0 ${
                    userCoins >= p.coin_price
                      ? "bg-purple-600 hover:bg-purple-700 text-white active:scale-95"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  <span>Buy {p.coin_price}</span>
                  <img src={PackageIcon} className="w-[14px] h-[14px] object-contain" alt="coin" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EngagementModal;
